import { supabase } from "@/integrations/supabase/client";

export type AllocationBasis = "documents" | "users";
export type CostType = "desarrollo" | "hosting" | "running";

export const COST_TYPE_LABELS: Record<CostType, string> = {
  desarrollo: "Desarrollo",
  hosting: "Hosting",
  running: "Running",
};

export interface DevelopmentCost {
  id: string;
  month: string; // YYYY-MM-DD (día 1)
  concept: string;
  amountEur: number;
  credits: number;
  allocationBasis: AllocationBasis;
  costType: CostType;
  notes: string | null;
}

export interface DevelopmentAllocationRow {
  companyId: string;
  name: string;
  documents: number;
  users: number;
  desarrolloEur: number;
  hostingEur: number;
  runningEur: number;
  eur: number;
  credits: number;
  share: number; // 0..1
}

export interface DevelopmentCostsSummary {
  costs: DevelopmentCost[];
  totalEur: number;
  totalCredits: number;
  monthEur: number;
  monthCredits: number;
  allocation: DevelopmentAllocationRow[];
  unassignedDocuments: number;
}

const monthKeyOf = (iso: string) => iso.slice(0, 7);

export const currentMonthStart = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const normalizeCostType = (v: string | null | undefined): CostType => {
  if (v === "hosting" || v === "running") return v;
  return "desarrollo";
};

const mapRow = (r: {
  id: string;
  month: string;
  concept: string;
  amount_eur: number | string;
  credits: number | string;
  allocation_basis: string;
  cost_type: string | null;
  notes: string | null;
}): DevelopmentCost => ({
  id: r.id,
  month: r.month,
  concept: r.concept,
  amountEur: Number(r.amount_eur) || 0,
  credits: Number(r.credits) || 0,
  allocationBasis: r.allocation_basis === "users" ? "users" : "documents",
  costType: normalizeCostType(r.cost_type),
  notes: r.notes,
});

export const listDevelopmentCosts = async (): Promise<DevelopmentCost[]> => {
  const { data, error } = await supabase
    .from("development_costs")
    .select("id, month, concept, amount_eur, credits, allocation_basis, cost_type, notes")
    .order("month", { ascending: false })
    .limit(500);
  if (error || !data) {
    console.error("listDevelopmentCosts:", error);
    return [];
  }
  return data.map(mapRow);
};

export const createDevelopmentCost = async (input: {
  month: string;
  concept: string;
  amountEur: number;
  credits: number;
  allocationBasis: AllocationBasis;
  costType: CostType;
  notes?: string | null;
}): Promise<boolean> => {
  const { data: userRes } = await supabase.auth.getUser();
  const { error } = await supabase.from("development_costs").insert({
    month: input.month,
    concept: input.concept,
    amount_eur: input.amountEur,
    credits: input.credits,
    allocation_basis: input.allocationBasis,
    cost_type: input.costType,
    notes: input.notes ?? null,
    created_by: userRes.user?.id ?? null,
  });
  if (error) {
    console.error("createDevelopmentCost:", error);
    return false;
  }
  return true;
};

export const deleteDevelopmentCost = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from("development_costs").delete().eq("id", id);
  if (error) {
    console.error("deleteDevelopmentCost:", error);
    return false;
  }
  return true;
};

interface MonthActivity {
  documentsByCompany: Map<string, number>;
  usersByCompany: Map<string, Set<string>>;
  totalDocuments: number;
  totalUsers: number;
}

/**
 * Actividad (documentos procesados y usuarios activos) por empresa y mes.
 */
const loadActivity = async (): Promise<Map<string, MonthActivity>> => {
  const byMonth = new Map<string, MonthActivity>();
  const { data, error } = await supabase
    .from("processing_events")
    .select("company_id, user_id, count, occurred_at, event_type")
    .eq("event_type", "processed")
    .limit(50000);
  if (error || !data) {
    console.error("loadActivity:", error);
    return byMonth;
  }
  for (const ev of data) {
    const mk = monthKeyOf(ev.occurred_at as string);
    const entry =
      byMonth.get(mk) ??
      ({
        documentsByCompany: new Map(),
        usersByCompany: new Map(),
        totalDocuments: 0,
        totalUsers: 0,
      } as MonthActivity);
    const cid = (ev.company_id as string | null) ?? "__sin_empresa__";
    const n = Number(ev.count) || 0;
    entry.documentsByCompany.set(cid, (entry.documentsByCompany.get(cid) ?? 0) + n);
    entry.totalDocuments += n;
    if (ev.user_id) {
      const set = entry.usersByCompany.get(cid) ?? new Set<string>();
      set.add(ev.user_id as string);
      entry.usersByCompany.set(cid, set);
    }
    byMonth.set(mk, entry);
  }
  for (const entry of byMonth.values()) {
    entry.totalUsers = [...entry.usersByCompany.values()].reduce((s, set) => s + set.size, 0);
  }
  return byMonth;
};

/**
 * Actividad acumulada de todos los meses (fallback para costes cuyo mes
 * aún no tiene documentos procesados).
 */
const aggregateActivity = (byMonth: Map<string, MonthActivity>): MonthActivity => {
  const agg: MonthActivity = {
    documentsByCompany: new Map(),
    usersByCompany: new Map(),
    totalDocuments: 0,
    totalUsers: 0,
  };
  for (const act of byMonth.values()) {
    for (const [cid, n] of act.documentsByCompany) {
      agg.documentsByCompany.set(cid, (agg.documentsByCompany.get(cid) ?? 0) + n);
      agg.totalDocuments += n;
    }
    for (const [cid, set] of act.usersByCompany) {
      const gset = agg.usersByCompany.get(cid) ?? new Set<string>();
      for (const u of set) gset.add(u);
      agg.usersByCompany.set(cid, gset);
    }
  }
  agg.totalUsers = [...agg.usersByCompany.values()].reduce((s, set) => s + set.size, 0);
  return agg;
};

/**
 * Costes introducidos manualmente + prorrateo por empresa
 * según documentos tramitados o usuarios activos del mes de cada coste.
 */
export const getDevelopmentCostsSummary = async (): Promise<DevelopmentCostsSummary> => {
  const [costs, activity] = await Promise.all([listDevelopmentCosts(), loadActivity()]);

  const thisMonth = monthKeyOf(currentMonthStart());
  let totalEur = 0;
  let totalCredits = 0;
  let monthEur = 0;
  let monthCredits = 0;

  const alloc = new Map<string, DevelopmentAllocationRow>();
  const ensure = (cid: string) =>
    alloc.get(cid) ??
    ({
      companyId: cid,
      name: cid,
      documents: 0,
      users: 0,
      desarrolloEur: 0,
      hostingEur: 0,
      runningEur: 0,
      eur: 0,
      credits: 0,
      share: 0,
    } as DevelopmentAllocationRow);

  for (const cost of costs) {
    totalEur += cost.amountEur;
    totalCredits += cost.credits;
    if (monthKeyOf(cost.month) === thisMonth) {
      monthEur += cost.amountEur;
      monthCredits += cost.credits;
    }

    const act = activity.get(monthKeyOf(cost.month)) ?? aggregateActivity(activity);
    if (act.totalDocuments <= 0 && act.totalUsers <= 0) continue;

    const weights = new Map<string, number>();
    let totalWeight = 0;
    if (cost.allocationBasis === "users") {
      for (const [cid, set] of act.usersByCompany) {
        weights.set(cid, set.size);
        totalWeight += set.size;
      }
    } else {
      for (const [cid, n] of act.documentsByCompany) {
        weights.set(cid, n);
        totalWeight += n;
      }
    }
    if (totalWeight <= 0) continue;

    for (const [cid, w] of weights) {
      const row = ensure(cid);
      const eurShare = (cost.amountEur * w) / totalWeight;
      if (cost.costType === "hosting") row.hostingEur += eurShare;
      else if (cost.costType === "running") row.runningEur += eurShare;
      else row.desarrolloEur += eurShare;
      row.eur += eurShare;
      row.credits += (cost.credits * w) / totalWeight;
      row.documents = Math.max(row.documents, 0);
      alloc.set(cid, row);
    }
  }

  // Documentos y usuarios acumulados (informativos)
  for (const act of activity.values()) {
    for (const [cid, n] of act.documentsByCompany) {
      const row = ensure(cid);
      row.documents += n;
      alloc.set(cid, row);
    }
    for (const [cid, set] of act.usersByCompany) {
      const row = ensure(cid);
      row.users = Math.max(row.users, set.size);
      alloc.set(cid, row);
    }
  }

  const rows = [...alloc.values()];
  const unassignedDocuments = rows.find((r) => r.companyId === "__sin_empresa__")?.documents ?? 0;
  const companyRows = rows.filter((r) => r.companyId !== "__sin_empresa__");
  const sumEur = companyRows.reduce((s, r) => s + r.eur, 0);
  for (const r of companyRows) r.share = sumEur > 0 ? r.eur / sumEur : 0;

  // Nombres de empresa
  const ids = companyRows.map((r) => r.companyId);
  if (ids.length) {
    const { data } = await supabase.from("company_masters").select("id, name").in("id", ids);
    const names = new Map((data ?? []).map((c) => [c.id, c.name]));
    for (const r of companyRows) {
      r.name = names.get(r.companyId) ?? "Empresa desconocida";
    }
  }

  return {
    costs,
    totalEur,
    totalCredits,
    monthEur,
    monthCredits,
    allocation: companyRows.filter((r) => r.eur > 0 || r.documents > 0).sort((a, b) => b.eur - a.eur),
    unassignedDocuments,
  };
};
